import { useFormikContext } from "formik";
import _ from "lodash";
import React from "react";
import { useSearchParents } from "src/generated/hooks/form-service/queries";
import { canonicalPhoneNumber } from "src/services/format";
import { isValidEmail, isValidPhoneNumber } from "src/services/formValidations";
import { ParentProfileFormType } from "../schemas";

const SEARCH_DEBOUNCE_MS = 350;
const RESULT_LIMIT = 10;

/**
 * Drives the possible-parent-duplicates search from the live parent form values.
 * Fires once a complete email or phone number is entered, matching the backend on
 * email/phone/name. Purely informational for the user.
 */
export function useParentDuplicateSearch(organizationId: string) {
  const { values } = useFormikContext<ParentProfileFormType>();
  const email = (values.email_address ?? "").trim();
  const phone = (values.phone_number ?? "").trim();

  const emailValid = isValidEmail(email);
  const phoneValid = isValidPhoneNumber(phone);
  const isQualified = emailValid || phoneValid;
  // Search by the complete email, else the canonical phone.
  const searchValue = emailValid
    ? email
    : phoneValid
    ? canonicalPhoneNumber(phone)
    : "";

  const [debouncedSearch, setDebouncedSearch] = React.useState(searchValue);
  const debouncedSet = React.useMemo(
    () =>
      _.debounce(
        (value: string) => setDebouncedSearch(value),
        SEARCH_DEBOUNCE_MS
      ),
    []
  );
  React.useEffect(() => {
    debouncedSet(searchValue);
    return () => debouncedSet.cancel();
  }, [searchValue, debouncedSet]);

  const { data, isLoading, isError } = useSearchParents(
    {
      query: {
        organizationId,
        search: debouncedSearch,
        limit: RESULT_LIMIT,
        offset: 0,
      },
    },
    undefined,
    {
      enabled: isQualified && !!organizationId && debouncedSearch.length >= 3,
    }
  );

  const matches = data?.data.parents ?? [];
  const hasSearched = isQualified && !isLoading && data !== undefined;
  const isPending =
    isQualified && (searchValue !== debouncedSearch || isLoading);

  return {
    isQualified,
    isLoading,
    isError,
    hasSearched,
    isPending,
    matches,
    fullName: [values.first_name, values.last_name].filter(Boolean).join(" "),
  };
}
